import type { TFunction } from "i18next";
import i18n from "i18next";
import { normalizeApiError } from "@maeiz/core";

export interface ApiFailure {
    status: number;
    code: string;
    message: string;
    /** Extra facts the server sent with the error (counts, required plan, …). */
    details: Record<string, unknown>;
}

/** Everything the UI needs from a failed request, in one shape. */
export function apiFailure(error: unknown): ApiFailure {
    const n = normalizeApiError(error);
    const body = (n.raw ?? {}) as {
        error?: { details?: Record<string, unknown> };
        [key: string]: unknown;
    };
    const nested = body.error?.details;
    const details: Record<string, unknown> =
        nested && typeof nested === "object" && !Array.isArray(nested) ? { ...nested } : {};
    // Some routes put their extra facts at the top level of the body instead.
    for (const key of ["riders", "customers", "shifts", "areas", "reason", "feature", "requiredPlan"]) {
        if (details[key] === undefined && body[key] !== undefined) details[key] = body[key];
    }
    return { status: n.status, code: n.code, message: n.message, details };
}

/** Turns the server's raw facts into readable values for the message templates. */
function errorParams(t: TFunction, details: Record<string, unknown>): Record<string, unknown> {
    const out: Record<string, unknown> = { ...details };
    if (typeof details.feature === "string") {
        const key = `errors.features.${details.feature}`;
        out.feature = i18n.exists(key) ? t(key) : details.feature;
    }
    if (typeof details.requiredPlan === "string") {
        out.plan = details.requiredPlan.charAt(0).toUpperCase() + details.requiredPlan.slice(1);
    }
    if (typeof details.resource === "string") {
        const key = `errors.resources.${details.resource}`;
        out.resource = i18n.exists(key) ? t(key) : details.resource;
    }
    return out;
}

/**
 * Codes the server derives from the HTTP status alone. The server's own sentence says what is
 * actually wrong ("Current password is incorrect"), so it is kept instead of a generic line.
 */
const GENERIC_STATUS_CODES = new Set(["VALIDATION_ERROR", "UNPROCESSABLE_ENTITY", "BAD_REQUEST", "CONFLICT"]);

/**
 * A message for a failed request. Known error codes are shown in the reader's language
 * (`errors.codes.<code>`); anything else falls back to the server's own text.
 */
export function apiErrorText(t: TFunction, error: unknown): string {
    const f = apiFailure(error);
    if (GENERIC_STATUS_CODES.has(f.code)) return f.message;
    const key = `errors.codes.${f.code}`;
    if (i18n.exists(key)) {
        return t(key, { ...errorParams(t, f.details), defaultValue: f.message }) as string;
    }
    return f.message;
}

export function isPlanFeatureError(error: unknown): boolean {
    return apiFailure(error).code === "PLAN_FEATURE_UNAVAILABLE";
}
